import { Button, useToast, Heading, Box, Input, Switch, Flex, Select } from '@chakra-ui/react';
import xhr from 'xhr';
import QuillEditor from 'components/editorQuill';
import React from 'react';

import ClozeEditor from './interactiveEditors/ClozeEditor';
import MatchingEditor from './interactiveEditors/MatchingEditor';

const EMPTY_CLOZE = { version: 1, prompt_html: '', blanks: [] };
const EMPTY_MATCHING = {
  version: 1,
  left_label: '',
  right_label: '',
  left: [],
  right: [],
  pairs: [],
};

const DEFAULT_PROMPTS = {
  cloze:
    '<p><strong>Lee con atención y completa cada espacio en blanco con la palabra correcta.</strong></p>',
  matching:
    '<p><strong>Empareja cada elemento de la columna izquierda con el correspondiente de la derecha. Toca un ítem y luego su par para confirmar.</strong></p>',
};

function isQuillEmpty(html) {
  const s = String(html || '').trim();
  return s === '' || s === '<p></p>' || s === '<p><br></p>';
}

function isDefaultPrompt(html) {
  const s = String(html || '').trim();
  return Object.values(DEFAULT_PROMPTS).some((d) => d.trim() === s);
}

function validateInteractive(type, config) {
  if (!type) return null;
  if (type === 'cloze') {
    if (!config || !Array.isArray(config.blanks) || config.blanks.length === 0) {
      return 'Marca al menos un espacio en el enunciado.';
    }
    for (const b of config.blanks) {
      if (b.mode === 'select') {
        const opts = (b.options || []).filter((o) => String(o || '').trim());
        if (opts.length < 2) {
          return `El espacio [${b.id}] necesita al menos 2 opciones.`;
        }
        if (!b.accepted_answers || !b.accepted_answers[0]) {
          return `El espacio [${b.id}] necesita una opción marcada como correcta.`;
        }
        if (!opts.includes(b.accepted_answers[0])) {
          return `El espacio [${b.id}]: la opción correcta debe coincidir con una de las opciones.`;
        }
      } else {
        if (!b.accepted_answers || b.accepted_answers.length === 0) {
          return `El espacio [${b.id}] necesita al menos una respuesta válida.`;
        }
      }
    }
  }
  if (type === 'matching') {
    if (!config) return 'Configura los pares de emparejamiento.';
    if (!config.left_label || !config.right_label) {
      return 'Define los nombres de las dos columnas.';
    }
    if (!Array.isArray(config.left) || config.left.length < 2) {
      return 'Agrega al menos 2 ítems en la columna izquierda.';
    }
    if (!Array.isArray(config.right) || config.right.length < 2) {
      return 'Agrega al menos 2 ítems en la columna derecha.';
    }
    const missingPair = config.left.find(
      (it) => !config.pairs.some((p) => p.leftId === it.id)
    );
    if (missingPair) {
      return 'Cada ítem de la columna izquierda debe emparejarse con uno de la derecha.';
    }
  }
  return null;
}

export default function InsertText(props) {
  const { setIsOpen2 } = props;

  const [inputText, setInputText] = React.useState('');
  const [activeAi, setActiveAi] = React.useState(false);

  const [inputTextVideo, setInputTextVideo] = React.useState('');

  const [textContent, setTextContent] = React.useState('');

  const toasts = useToast();

  const modalRef = React.useRef();

  const [loadingButton, setLoadingButton] = React.useState(false);
  const [interactiveType, setInteractiveType] = React.useState('');
  const [clozeConfig, setClozeConfig] = React.useState(EMPTY_CLOZE);
  const [matchingConfig, setMatchingConfig] = React.useState(EMPTY_MATCHING);

  React.useEffect(() => {
    const a = props.materialModal.activity;
    setInputText(a?.title);
    setInputTextVideo(a?.video);
    setTextContent(a?.content);
    setActiveAi(a?.ai);
    const it = a?.interactive_type || '';
    setInteractiveType(it);
    const cfg = a?.interactive_config || null;
    if (it === 'cloze') {
      setClozeConfig(cfg && cfg.blanks ? cfg : EMPTY_CLOZE);
    } else {
      setClozeConfig(EMPTY_CLOZE);
    }
    if (it === 'matching') {
      setMatchingConfig(cfg && cfg.left ? cfg : EMPTY_MATCHING);
    } else {
      setMatchingConfig(EMPTY_MATCHING);
    }
  }, [props.materialModal.id]);

  const handleUpdateActivity = async () => {
    try {
      setLoadingButton(true);

      if (inputText == '') return alert('el titulo está vacio');

      if (textContent == '') return alert('el texto está vacio');

      const activeConfig =
        interactiveType === 'cloze'
          ? clozeConfig
          : interactiveType === 'matching'
          ? matchingConfig
          : null;
      const interactiveError = validateInteractive(interactiveType, activeConfig);
      if (interactiveError) {
        alert(interactiveError);
        return;
      }

      await xhr.put(`/activities/${props.materialModal.activity.id}`, {
        data: {
          title: inputText,
          content: textContent,
          video: inputTextVideo,
          ai: activeAi,
          interactive_type: interactiveType || null,
          interactive_config: activeConfig,
        },
      });

      const responseMaterials = await xhr.put(`/materials/${props.materialModal.id}`, {
        data: {
          title: inputText,
        },
      });
      const materialPopulate = await xhr(`/materials/${responseMaterials.data.data.id}?populate=activity`);

      toasts({
        title: 'La actividad actualizó correctamente!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      props.updateMaterials(materialPopulate.data.data);

      setIsOpen2(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingButton(false);
    }
  };

  return (
    <div>
      <Box marginBottom="20px" position="relative" top="-10px">
        <Heading use="h3" width="100%">
          Actividad {props.materialModal.activity.id}
        </Heading>
      </Box>
      <Box p="16px" bg="#f3f1ff" borderLeft="3px solid #574feb" borderRadius="6px">
        <Box fontSize="14px" fontWeight="500" color="#3c3489" mb="8px">
          Tipo interactivo
        </Box>
        <Select
          bg="white"
          value={interactiveType}
          onChange={(e) => {
            const next = e.target.value;
            setInteractiveType(next);
            if (next) {
              if (!interactiveType) setActiveAi(true);
              if (
                DEFAULT_PROMPTS[next] &&
                (isQuillEmpty(textContent) || isDefaultPrompt(textContent))
              ) {
                setTextContent(DEFAULT_PROMPTS[next]);
              }
            }
          }}
        >
          <option value="">Ninguno (texto / archivos)</option>
          <option value="cloze">Completar espacios (Cloze)</option>
          <option value="matching">Emparejar columnas</option>
        </Select>
        <Box fontSize="12px" color="gray.500" mt="6px">
          Cambiar el tipo no borra la configuración actual hasta que guardes.
        </Box>
      </Box>

      <Box mt="16px" mb="10px">
        <Flex gap={3} alignItems="center">
          <Box>
            {interactiveType
              ? 'Incluir retroalimentación con IA?'
              : 'Calificable con AI?'}
          </Box>
          <Switch isChecked={activeAi} onChange={(e) => setActiveAi(!activeAi)} id="email-alerts" />
        </Flex>
        {interactiveType && (
          <Box fontSize="12px" color="gray.500" mt="6px">
            El score se calcula automáticamente. Activa esta opción para generar también un mensaje de retroalimentación con IA bajo el nombre del profesor.
          </Box>
        )}
      </Box>

      <Box mt="20px">Título</Box>
      <Input
        mt="10px"
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
      />

      <Box mt="20px">
        {interactiveType ? 'Instrucciones para el estudiante' : 'Texto'}
      </Box>
      <QuillEditor value={textContent} setValue={setTextContent} />

      {interactiveType === 'cloze' && (
        <Box mt="16px">
          <ClozeEditor value={clozeConfig} onChange={setClozeConfig} />
        </Box>
      )}

      {interactiveType === 'matching' && (
        <Box mt="16px">
          <MatchingEditor value={matchingConfig} onChange={setMatchingConfig} />
        </Box>
      )}

      <Box mt="20px">Vídeo</Box>
      <Input
        mt="10px"
        mb="10px"
        value={inputTextVideo === null ? '' : inputTextVideo}
        onChange={(e) => setInputTextVideo(e.target.value)}
      />

      <Button
        type="submit"
        colorScheme="#574feb"
        bg="#574feb"
        marginY="10px"
        marginX="10px"
        width="140px"
        onClick={handleUpdateActivity}
        isLoading={loadingButton}
      >
        Actualizar
      </Button>
      <style jsx global>{`
        .cdkEditorUpdate {
          width: 100%;
        }
        .cdkEditorUpdate .ck.ck-editor__main > .ck-editor__editable {
          height: 500px;
        }
      `}</style>
    </div>
  );
}
